import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AuthenticationError, AuthorizationParams } from "@auth0/auth0-angular";
import { NotificationService } from "@kno2/common/services/notifications";
import { ACCESS_CHANGED_STORAGE_KEY, AuthService } from "@kno2/core/auth";
import { WINDOW_TOKEN } from "@kno2/core/browser";
import { APP_CONFIG_TOKEN } from "@kno2/core/config";
import { SessionService } from "@kno2/core/session";
import { OrganizationAuthOption, OrganizationAuthRequirements, UserProfile, UserProfileOrganization } from "@kno2/core/session/user-profile.model";
import { SessionStorageService } from "@kno2/core/storage";
import { AccountService } from "@kno2/data-access/account";
import { DialogService } from "@kno2/shared/dialog";
import { OrgSwitcherSignInModalComponent } from "./org-switcher-sign-in-modal.component";

export type OrgSwitcherMode = "silent" | "signIn" | "unavailable";

interface PendingOrgSwitch {
    organizationId: string;
    organizationName: string;
    requirements: OrganizationAuthRequirements | null;
    attempt: "silent" | "interactive";
    previousAuthTime?: number;
}

const PENDING_SWITCH_STORAGE_KEY = "orgSwitch.pending";
const CONNECTION_CLAIM_RENEWED_STORAGE_KEY = "orgSwitch.connectionClaimRenewed";
const ORGANIZATION_MOVED_STORAGE_KEY = "orgSwitch.organizationMoved";
const SILENT_SWITCH_INTERACTIVE_ERRORS = ["access_denied", "login_required", "interaction_required", "consent_required"];

@Injectable({
    providedIn: "root"
})
export class OrgSwitcherService {
    private readonly authService = inject(AuthService);
    private readonly sessionService = inject(SessionService);
    private readonly accountService = inject(AccountService);
    private readonly dialogService = inject(DialogService);
    private readonly notificationService = inject(NotificationService);
    private readonly sessionStorageService = inject(SessionStorageService);
    private readonly router = inject(Router);
    private readonly appConfig = inject(APP_CONFIG_TOKEN);
    private readonly window = inject(WINDOW_TOKEN);

    public constructor() {
        this.authService.tokenOrganizationChanged$.subscribe(() => this.onTokenOrganizationChanged());
        this.authService.tokenConnectionChanged$.subscribe(() => this.sessionService.refreshProfile().catch(console.error));
    }

    public modeFor(organization: UserProfileOrganization): OrgSwitcherMode {
        if (!organization.authRequirements) {
            return organization.isAccessible === false ? "unavailable" : "silent";
        }

        return organization.authRequirements.silent ? "silent" : "signIn";
    }

    public async switchSilently(organization: UserProfileOrganization): Promise<void> {
        const token = await this.accountService.changeOrganization(organization.id);
        if (!token) {
            this.setPending(organization, "silent");
            this.sessionService.removeProfile();
            await this.redirectToSignIn({ prompt: "none" });
            return;
        }

        await this.sessionService.refreshProfile();
        await this.router.navigate([this.sessionService.getDefaultRouteForActiveRole()]);
    }

    public async signInTo(organization: UserProfileOrganization): Promise<void> {
        await this.promptForSignIn({
            organizationId: organization.id,
            organizationName: organization.name,
            requirements: organization.authRequirements ?? null,
            attempt: "interactive"
        });
    }

    public async resumeAfterSilentDenial(error: AuthenticationError | null): Promise<boolean> {
        const pending = this.getPending();
        if (pending?.attempt !== "silent") return false;

        this.sessionStorageService.remove(PENDING_SWITCH_STORAGE_KEY);
        if (!SILENT_SWITCH_INTERACTIVE_ERRORS.includes(error?.error)) return false;

        const signingIn = await this.promptForSignIn({ ...pending, attempt: "interactive" });
        if (!signingIn) await this.router.navigate(["/"]);

        return true;
    }

    private completing: Promise<void> | null = null;

    public completeAfterSignIn(): Promise<void> {
        this.completing ??= this.complete().finally(() => (this.completing = null));

        return this.completing;
    }

    private redirecting = false;

    public get redirectingToSignIn(): boolean {
        return this.redirecting;
    }

    private async redirectToSignIn(params: AuthorizationParams): Promise<void> {
        this.redirecting = true;
        try {
            await this.authService.login(params);
        } catch (error) {
            this.redirecting = false;
            throw error;
        }
    }

    private async complete(): Promise<void> {
        if (this.redirecting) return;

        if (this.sessionStorageService.get<boolean>(ACCESS_CHANGED_STORAGE_KEY)) {
            this.sessionStorageService.remove(ACCESS_CHANGED_STORAGE_KEY);
            this.notificationService.warning("Your access to the organization you were using changed, so you were asked to sign in again.");
        }

        await this.ensureConnectionClaim().catch(console.error);
        await this.ensureProfileMatchesToken().catch(console.error);
        if (!this.profileMatchesToken()) return;

        if (this.sessionStorageService.get<boolean>(ORGANIZATION_MOVED_STORAGE_KEY)) {
            this.sessionStorageService.remove(ORGANIZATION_MOVED_STORAGE_KEY);
            this.notificationService.warning(
                `You're now in ${this.sessionService.getProfile().organizationName}. Your access changed or you switched organizations elsewhere.`
            );
        }

        try {
            await this.completePendingSwitch();
        } finally {
            if (!this.redirecting) this.sessionService.confirmProfile();
        }
    }

    private async completePendingSwitch(): Promise<void> {
        const pending = this.getPending();
        if (!pending) return;
        this.sessionStorageService.remove(PENDING_SWITCH_STORAGE_KEY);

        const profile = this.sessionService.getProfile();
        if (pending.attempt === "silent") {
            if (profile.organizationId !== pending.organizationId) this.notificationService.warning(this.silentSwitchMissedReason(pending, profile));
            return;
        }

        const authenticatedAt = await this.authService.getAuthenticatedAt();
        if (pending.previousAuthTime !== undefined && (authenticatedAt === null || authenticatedAt <= pending.previousAuthTime)) return;

        if (profile.organizationId === pending.organizationId) return;

        const target = profile.organizations.find((organization) => organization.id === pending.organizationId);
        if (target && this.modeFor(target) === "silent") {
            await this.switchSilently(target);
            return;
        }

        this.notificationService.warning(
            `You signed in, but this account can't open ${pending.organizationName} through the sign-in you used. You're in ${profile.organizationName}.`
        );
    }

    private silentSwitchMissedReason(pending: PendingOrgSwitch, profile: UserProfile): string {
        const target = profile.organizations.find((organization) => organization.id === pending.organizationId);
        const landed = `You're in ${profile.organizationName}.`;

        if (!target) return `You no longer have access to ${pending.organizationName}. ${landed}`;
        if (this.modeFor(target) === "silent") return `${pending.organizationName} doesn't allow sign-in from your current IP address. ${landed}`;
        if (this.modeFor(target) === "signIn") return `${pending.organizationName} needs a different sign-in. Select it again to sign in. ${landed}`;

        return `You couldn't be switched to ${pending.organizationName}. ${landed}`;
    }

    private async promptForSignIn(pending: PendingOrgSwitch): Promise<boolean> {
        const { result } = await this.dialogService.open<OrganizationAuthOption>(OrgSwitcherSignInModalComponent, {
            headerText: `Sign in to ${pending.organizationName}`,
            dialogId: "org-switcher-sign-in",
            inputValues: {
                organizationName: pending.organizationName,
                options: pending.requirements?.options ?? [],
                passwordScope: pending.requirements?.passwordScope ?? null
            }
        });
        if (!result) return false;

        const previousAuthTime = (await this.authService.getAuthenticatedAt()) ?? 0;
        this.sessionStorageService.set(PENDING_SWITCH_STORAGE_KEY, { ...pending, previousAuthTime } satisfies PendingOrgSwitch);
        this.sessionService.removeProfile();
        await this.redirectToSignIn(this.signInParams(result));

        return true;
    }

    private signInParams(option: OrganizationAuthOption): AuthorizationParams {
        switch (option.kind) {
            case "saml":
                return { connection: option.connectionName, prompt: "login" };
            case "pcc":
                return { connection: this.appConfig.auth0.pccConnection ?? "PointClickCare", prompt: "login" };
            default:
                return { prompt: "login" };
        }
    }

    private async ensureConnectionClaim(): Promise<void> {
        const profile = this.sessionService.getProfile();
        if (!profile) return;

        if (profile.authConnection) {
            const tokenConnection = this.authService.tokenConnection();
            if (tokenConnection && tokenConnection !== profile.authConnection) await this.sessionService.refreshProfile();
            return;
        }

        if (this.authService.tokenConnection()) {
            await this.sessionService.refreshProfile();
            return;
        }

        if (!this.authService.isAuth0Token() || this.sessionStorageService.get<boolean>(CONNECTION_CLAIM_RENEWED_STORAGE_KEY)) return;

        this.sessionStorageService.set(CONNECTION_CLAIM_RENEWED_STORAGE_KEY, true);
        await this.authService.forceRenewTokens();
        await this.sessionService.refreshProfile();
    }

    private async ensureProfileMatchesToken(): Promise<void> {
        if (this.profileMatchesToken()) return;

        await this.sessionService.refreshProfile();
    }

    private profileMatchesToken(): boolean {
        const tokenOrganizationId = this.authService.tokenOrganizationId();
        const profile = this.sessionService.getProfile();

        return !tokenOrganizationId || !profile?.kno2OrganizationId || String(profile.kno2OrganizationId) === tokenOrganizationId;
    }

    private onTokenOrganizationChanged(): void {
        this.sessionStorageService.set(ORGANIZATION_MOVED_STORAGE_KEY, true);
        this.window.location.assign("/");
    }

    private setPending(organization: UserProfileOrganization, attempt: PendingOrgSwitch["attempt"]): void {
        this.sessionStorageService.set(PENDING_SWITCH_STORAGE_KEY, {
            organizationId: organization.id,
            organizationName: organization.name,
            requirements: organization.authRequirements ?? null,
            attempt
        } satisfies PendingOrgSwitch);
    }

    private getPending(): PendingOrgSwitch | null {
        const pending = this.sessionStorageService.get<PendingOrgSwitch>(PENDING_SWITCH_STORAGE_KEY);
        if (!pending || typeof pending.organizationId !== "string" || (pending.attempt !== "silent" && pending.attempt !== "interactive")) return null;

        return pending;
    }
}
